/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ApplicationInsights } from '@microsoft/applicationinsights-web';

/**
 * Thin wrapper around Application Insights so both Vue components and Vuex
 * stores can emit telemetry through a single, safe entry point.
 *
 * The client is registered by `plugins/ai.client.ts` once App Insights has been
 * initialized. If App Insights is not configured (no key) or fails to load,
 * every call here is a no-op — telemetry must never break the enrollment flow.
 */
let client: ApplicationInsights | undefined;

export function setTelemetryClient(instance: ApplicationInsights | undefined): void {
    client = instance;
}

function sanitize(properties?: Record<string, unknown>): Record<string, string> | undefined {
    if (!properties) return undefined;
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(properties)) {
        if (value === undefined || value === null) continue;
        out[key] = typeof value === 'string' ? value : String(value);
    }
    return out;
}

export function trackEvent(name: string, properties?: Record<string, unknown>): void {
    if (!client) return;
    try {
        client.trackEvent({ name }, sanitize(properties));
    } catch {
        /* never let telemetry throw into the caller */
    }
}

export function trackException(error: unknown, properties?: Record<string, unknown>): void {
    if (!client) return;
    try {
        const exception = error instanceof Error ? error : new Error(String(error));
        client.trackException({ exception, properties: sanitize(properties) });
    } catch {
        /* never let telemetry throw into the caller */
    }
}
